import { useId, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { ApiClientError } from '@olmt/shared'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { defaultDeviceName, platformName } from '../lib/device'
import { t, useT } from '../lib/i18n'
import { Spinner } from '../components/Spinner'

/**
 * The front door (docs/CONTRACT.md §4 *Auth*, §11).
 *
 * The platform is open to sign up and closed to use, so the primary surface is sign up / sign in;
 * an invite code — the family's fast lane and the only way in without an email — is the secondary
 * path behind "Have an invite?". Both land a device token and let the root layout decide between
 * the app and the waiting screen.
 */

type Mode = 'signup' | 'signin'

/** One sentence for each way the front door can fail, chosen by the api's error code. */
function authErrorMessage(caught: unknown): string {
  if (!(caught instanceof ApiClientError)) return t('auth.errorGeneric')
  switch (caught.code) {
    case 'email_taken':
      return t('auth.errorEmailTaken')
    case 'bad_credentials':
      return t('auth.errorBadCredentials')
    case 'rate_limited':
      return t('auth.errorRateLimited')
    case 'network':
      return t('auth.errorNetwork')
    case 'too_large':
      return t('auth.errorTooLarge')
    default:
      return t('auth.errorGeneric')
  }
}

function AuthShell({ children }: { children: ReactNode }) {
  const t = useT()
  return (
    /* `my-auto` on the column, not `justify-center` on the row: with `justify-center` a flex
       container that overflows pushes the excess off *both* ends, and the start end cannot be
       scrolled back to (scrollTop cannot go negative). Measured on the shipped build at 1280×844:
       the sign-up form is 44px taller than the viewport, so the lamp and the wordmark were cut off
       with no way to reach them — on the first screen the family ever sees. Auto margins centre the
       column when it fits and leave the top at 0 when it does not. */
    <div className="app-shell items-center overflow-y-auto">
      <main className="my-auto w-full max-w-sm px-6 py-10">
        {/* The front door. One warm point of light, the name of the house, one sentence about what
            this place is, then the fields that get you in. */}
        <span
          aria-hidden="true"
          className="mb-5 flex size-12 items-center justify-center rounded-full bg-accent/12 ring-1 ring-accent/30 ring-inset"
        >
          <span className="size-3 rounded-full bg-accent shadow-[0_0_16px_3px_rgb(232_176_75/0.45)]" />
        </span>
        <h1 className="text-display font-semibold tracking-tight text-ink">
          olmt<span className="text-accent">·</span>call
        </h1>
        <p className="mt-2 text-ui text-muted">{t('auth.tagline')}</p>
        {children}
      </main>
    </div>
  )
}

type FieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'className'>

function Field({ label, value, onChange, hint, ...input }: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  return (
    <div className="flex flex-col gap-1.5 text-meta text-muted">
      {/* The label stays the control's name; the hint is a description, so it never becomes part of
          what a screen reader reads out as the field's label. */}
      <label htmlFor={id}>{label}</label>
      <input
        {...input}
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={hint ? hintId : undefined}
        className="min-h-12 rounded-panel bg-surface2 px-3.5 text-body text-ink ring-1 ring-line ring-inset placeholder:text-muted"
      />
      {hint && (
        <span id={hintId} className="text-meta text-muted">
          {hint}
        </span>
      )}
    </div>
  )
}

/** The device name is only so the user can recognise this phone in their own device list. */
function DeviceNameField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const t = useT()
  return (
    <Field
      label={t('auth.deviceName')}
      value={value}
      onChange={onChange}
      maxLength={64}
      hint={t('auth.deviceNameHint')}
    />
  )
}

function SubmitButton({ busy, spinnerLabel, children }: { busy: boolean; spinnerLabel: string; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="mt-1 flex min-h-12 items-center justify-center gap-2 rounded-full bg-accent text-body font-semibold press text-on-accent hover:bg-accent-strong disabled:opacity-50"
    >
      {busy && <Spinner size={18} label={spinnerLabel} />}
      {children}
    </button>
  )
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p role="alert" className="text-ui text-danger">
      {message}
    </p>
  )
}

export function AuthPage() {
  const token = useAuthStore((state) => state.token)
  const [mode, setMode] = useState<Mode>('signup')
  const [invite, setInvite] = useState(false)
  const t = useT()

  if (token) return <Navigate to="/" replace />

  return (
    <AuthShell>
      {invite ? (
        <InviteForm onBack={() => setInvite(false)} />
      ) : (
        <>
          <div role="group" aria-label={t('auth.modeGroup')} className="mt-8 flex gap-2">
            {(
              [
                ['signup', 'auth.createAccount'],
                ['signin', 'auth.signIn'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={mode === key}
                onClick={() => setMode(key)}
                className={`min-h-11 flex-1 rounded-full text-ui font-medium ring-1 ring-inset transition-colors duration-quick ${
                  mode === key ? 'bg-surface3 text-ink ring-line-strong' : 'bg-surface2 text-muted ring-line hover:bg-surface3'
                }`}
              >
                {t(label)}
              </button>
            ))}
          </div>

          {mode === 'signup' ? <SignUpForm /> : <SignInForm />}

          <button
            type="button"
            onClick={() => setInvite(true)}
            className="mt-6 min-h-11 text-ui text-muted underline decoration-line-strong underline-offset-4 hover:text-ink"
          >
            {t('auth.haveInvite')}
          </button>
        </>
      )}
    </AuthShell>
  )
}

function SignUpForm() {
  const navigate = useNavigate()
  const t = useT()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [deviceName, setDeviceName] = useState(() => defaultDeviceName())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    const name = displayName.trim()
    const address = email.trim()
    if (!name) {
      setError(t('auth.errorDisplayName'))
      return
    }
    if (!address) {
      setError(t('auth.errorEmail'))
      return
    }
    if (password.length < 8) {
      setError(t('auth.errorPasswordShort'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { token, user } = await api.signup({
        email: address,
        password,
        displayName: name,
        deviceName: deviceName.trim() || defaultDeviceName(),
        platform: platformName(),
      })
      useAuthStore.getState().setSession(token, user)
      navigate('/', { replace: true })
    } catch (caught) {
      setError(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
      <Field label={t('auth.displayName')} value={displayName} onChange={setDisplayName} autoComplete="name" maxLength={40} />
      <Field
        label={t('auth.email')}
        value={email}
        onChange={setEmail}
        type="email"
        inputMode="email"
        autoComplete="email"
        spellCheck={false}
        maxLength={254}
      />
      <Field
        label={t('auth.password')}
        value={password}
        onChange={setPassword}
        type="password"
        autoComplete="new-password"
        minLength={8}
        maxLength={200}
        hint={t('auth.passwordHint')}
      />
      <DeviceNameField value={deviceName} onChange={setDeviceName} />

      <FormError message={error} />

      <SubmitButton busy={busy} spinnerLabel={t('auth.creatingAccount')}>
        {t('auth.createAccount')}
      </SubmitButton>

      <p className="text-meta text-muted">{t('auth.signupNotice')}</p>
    </form>
  )
}

function SignInForm() {
  const navigate = useNavigate()
  const t = useT()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [deviceName, setDeviceName] = useState(() => defaultDeviceName())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    const address = email.trim()
    if (!address || !password) {
      setError(t('auth.errorCredentials'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { token, user } = await api.login({
        email: address,
        password,
        deviceName: deviceName.trim() || defaultDeviceName(),
        platform: platformName(),
      })
      useAuthStore.getState().setSession(token, user)
      navigate('/', { replace: true })
    } catch (caught) {
      setError(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
      <Field
        label={t('auth.email')}
        value={email}
        onChange={setEmail}
        type="email"
        inputMode="email"
        autoComplete="email"
        spellCheck={false}
        maxLength={254}
      />
      <Field
        label={t('auth.password')}
        value={password}
        onChange={setPassword}
        type="password"
        autoComplete="current-password"
        maxLength={200}
      />
      <DeviceNameField value={deviceName} onChange={setDeviceName} />

      <FormError message={error} />

      <SubmitButton busy={busy} spinnerLabel={t('auth.signingIn')}>
        {t('auth.signIn')}
      </SubmitButton>
    </form>
  )
}

type InviteFormProps = {
  /** Present for `/i/:code`; typed manually on the front door. */
  initialCode?: string
  /** Present on the front door, where the invite path is one of two; absent on `/i/:code`. */
  onBack?: () => void
}

function InviteForm({ initialCode = '', onBack }: InviteFormProps) {
  const navigate = useNavigate()
  const t = useT()
  const [code, setCode] = useState(initialCode)
  const [displayName, setDisplayName] = useState('')
  const [deviceName, setDeviceName] = useState(() => defaultDeviceName())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    const trimmedCode = code.trim()
    const trimmedName = displayName.trim()
    if (!trimmedCode) {
      setError(t('auth.errorInviteCode'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { token, user } = await api.redeemInvite({
        code: trimmedCode,
        // Omitted when blank: a recovery code is bound to an existing user and ignores it, while a
        // new account needs it. Sending it (or not) must not tell the user which kind they hold.
        displayName: trimmedName === '' ? undefined : trimmedName,
        deviceName: deviceName.trim() || defaultDeviceName(),
        platform: platformName(),
      })
      useAuthStore.getState().setSession(token, user)
      navigate('/', { replace: true })
    } catch (caught) {
      // An ordinary invite with no name is the one bad_request the form itself can prevent.
      if (caught instanceof ApiClientError && caught.message.includes('displayName')) {
        setError(t('auth.errorInviteDisplayName'))
      } else if (caught instanceof ApiClientError && caught.status === 404) {
        setError(t('auth.errorInviteInvalid'))
      } else if (caught instanceof ApiClientError && caught.status === 401) {
        setError(t('auth.errorInviteInvalid'))
      } else {
        setError(authErrorMessage(caught))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
      <h2 className="text-title font-semibold text-ink">{t('auth.joinTitle')}</h2>
      <p className="-mt-2 text-meta text-muted">{t('auth.joinNotice')}</p>

      <Field
        label={t('auth.inviteCode')}
        value={code}
        onChange={setCode}
        autoComplete="off"
        spellCheck={false}
        hint={t('auth.inviteCodeHint')}
      />
      <Field label={t('auth.displayName')} value={displayName} onChange={setDisplayName} autoComplete="name" maxLength={64} />
      <DeviceNameField value={deviceName} onChange={setDeviceName} />

      <FormError message={error} />

      <SubmitButton busy={busy} spinnerLabel={t('auth.joining')}>
        {t('auth.join')}
      </SubmitButton>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="min-h-11 text-ui text-muted underline decoration-line-strong underline-offset-4 hover:text-ink"
        >
          {t('auth.backToSignIn')}
        </button>
      )}
    </form>
  )
}

export function InvitePage() {
  const { code } = useParams<{ code: string }>()
  const token = useAuthStore((state) => state.token)

  if (token) return <Navigate to="/" replace />

  return (
    <AuthShell>
      <InviteForm initialCode={code ?? ''} />
    </AuthShell>
  )
}
